import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useLang } from '../context/LangContext';
import { IMAGES } from '../data/menuData';
import Footer from '../components/Footer';
import './Reservations.css';

function Calendar({ t, selectedDate, onSelectDate }) {
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const months = t('cal.months').split(',');
  const days = t('cal.days').split(',');
  const today = new Date(); today.setHours(0,0,0,0);

  const changeMonth = (dir) => {
    setViewDate(d => {
      const n = new Date(d);
      n.setMonth(n.getMonth() + dir);
      return n;
    });
  };

  const isCurrentMonth =
    viewDate.getFullYear() === today.getFullYear() &&
    viewDate.getMonth() === today.getMonth();

  const firstDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();
  const totalDays = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();

  const isSelected = (d) => selectedDate &&
    selectedDate.getDate() === d &&
    selectedDate.getMonth() === viewDate.getMonth() &&
    selectedDate.getFullYear() === viewDate.getFullYear();

  const isToday = (d) => {
    const t2 = new Date(); t2.setHours(0,0,0,0);
    return t2.getDate() === d && t2.getMonth() === viewDate.getMonth() && t2.getFullYear() === viewDate.getFullYear();
  };

  const isPast = (d) => new Date(viewDate.getFullYear(), viewDate.getMonth(), d) < today;

  return (
    <div className="calendar">
      <div className="cal-header">
        <button className="cal-nav" onClick={() => changeMonth(-1)} disabled={isCurrentMonth}>‹</button>
        <span className="cal-month">{months[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
        <button className="cal-nav" onClick={() => changeMonth(1)}>›</button>
      </div>
      <div className="cal-grid">
        {days.map(d => <div key={d} className="cal-day-label">{d}</div>)}
        {Array.from({length: firstDay}).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({length: totalDays}, (_, i) => i + 1).map(d => (
          <div
            key={d}
            className={`cal-day${isPast(d) ? ' disabled' : ''}${isSelected(d) ? ' selected' : ''}${isToday(d) && !isSelected(d) ? ' today' : ''}`}
            onClick={() => !isPast(d) && onSelectDate(new Date(viewDate.getFullYear(), viewDate.getMonth(), d))}
          >
            {d}
          </div>
        ))}
      </div>
    </div>
  );
}

const LUNCH_TIMES = ['12h00', '12h30', '13h00', '13h30'];
const DINNER_TIMES = ['19h00', '19h30', '20h00', '20h30', '21h00', '21h30'];

export default function Reservations() {
  const { t } = useLang();
  const [selectedDate, setSelectedDate] = useState(null);
  const [guests, setGuests] = useState(2);
  const [occasion, setOccasion] = useState('dinner');
  const [selectedTime, setSelectedTime] = useState('19h00');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [special, setSpecial] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const months = t('cal.months').split(',');

  const summaryDate = selectedDate
    ? `${selectedDate.getDate()} ${months[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
    : t('cal.not_selected');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate) {
      setSubmitError(t('val.no_date'));
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: `${firstName} ${lastName}`,
          from_email: email,
          reply_to: email,
          phone,
          date: summaryDate,
          time: selectedTime,
          guests,
          occasion: t(`occ.${occasion}`),
          special_requests: special || '—',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setSubmitted(true);
    } catch {
      setSubmitError(t('val.send_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="res-page">
      {/* Hero */}
      <div className="res-hero">
        <div className="section-label center">{t('res.eyebrow')}</div>
        <h1>{t('res.title')}</h1>
        <p>{t('res.desc')}</p>
      </div>

      <div className="res-grid">
        {/* ── LEFT PANELS ── */}
        <div className="res-left">

          {/* Panel 1: Date + Guests */}
          <div className="res-panel">
            <div className="res-panel-title">{t('res.date.title')}</div>
            <div className="date-guests-grid">
              <Calendar t={t} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
              <div className="guests-col">
                <div className="field-label">{t('res.guests')}</div>
                <div className="guest-row">
                  <button className="guest-btn" onClick={() => setGuests(g => Math.max(1, g - 1))}>−</button>
                  <span className="guest-count">{guests}</span>
                  <button className="guest-btn" onClick={() => setGuests(g => Math.min(20, g + 1))}>+</button>
                </div>
                <div className="field-label" style={{marginTop:'1.2rem'}}>{t('res.occasion')}</div>
                <select className="occasion-select" value={occasion} onChange={e => setOccasion(e.target.value)}>
                  <option value="dinner">{t('occ.dinner')}</option>
                  <option value="lunch">{t('occ.lunch')}</option>
                  <option value="birthday">{t('occ.birthday')}</option>
                  <option value="business">{t('occ.business')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Panel 2: Time slots */}
          <div className="res-panel">
            <div className="res-panel-title">{t('res.times.title')}</div>
            <div className="time-group">
              <div className="time-group-label">{t('res.lunch')}</div>
              <div className="time-slots">
                {LUNCH_TIMES.map(time => (
                  <button key={time} className={`time-slot${selectedTime === time ? ' selected' : ''}`} onClick={() => setSelectedTime(time)}>{time}</button>
                ))}
              </div>
            </div>
            <div className="time-group">
              <div className="time-group-label">{t('res.dinner')}</div>
              <div className="time-slots">
                {DINNER_TIMES.map(time => (
                  <button key={time} className={`time-slot${selectedTime === time ? ' selected' : ''}`} onClick={() => setSelectedTime(time)}>{time}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Panel 3: Contact form OR inline confirmation */}
          {submitted ? (
            <div className="res-panel res-confirm">
              <div className="res-confirm-icon">✓</div>
              <h2 className="res-confirm-heading">{t('confirm.heading')}</h2>
              <p className="res-confirm-sub">{t('confirm.sub')}</p>
              <div className="res-confirm-details">
                <div className="summary-row"><span>📅</span><span>{summaryDate}</span></div>
                <div className="summary-row"><span>🕐</span><span>{selectedTime}</span></div>
                <div className="summary-row"><span>👥</span><span>{guests} {t('summary.guests_label')}</span></div>
                <div className="summary-row"><span>🍽️</span><span>{t(`occ.${occasion}`)}</span></div>
                <div className="summary-row"><span>📧</span><span>{email}</span></div>
              </div>
            </div>
          ) : (
            <form className="res-panel" onSubmit={handleSubmit}>
              <div className="res-panel-title">{t('res.details.title')}</div>
              <div className="form-row">
                <div className="form-field">
                  <label>{t('form.fname')}</label>
                  <input type="text" required value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={t('form.fname')} />
                </div>
                <div className="form-field">
                  <label>{t('form.lname')}</label>
                  <input type="text" required value={lastName} onChange={e => setLastName(e.target.value)} placeholder={t('form.lname')} />
                </div>
              </div>
              <div className="form-field">
                <label>{t('form.email')}</label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder={t('form.email.placeholder')} />
              </div>
              <div className="form-field">
                <label>{t('form.phone')}</label>
                <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('form.phone.placeholder')} />
              </div>
              <div className="form-field">
                <label>{t('form.special')}</label>
                <textarea value={special} onChange={e => setSpecial(e.target.value)} placeholder={t('form.special.placeholder')} />
              </div>
              {submitError && <p className="submit-error">{submitError}</p>}
              <button type="submit" className="btn-confirm" disabled={submitting}>
                {submitting ? t('res.sending') : `${t('res.confirm')} ✓`}
              </button>
            </form>
          )}
        </div>

        {/* ── RIGHT SIDEBAR ── */}
        <div className="res-right">
          {/* Map card */}
          <div className="res-map-card">
            <div className="res-map-bg" style={{ backgroundImage: `url(${IMAGES.map})` }} />
            <div className="res-map-overlay">
              <button
                className="btn-open-map"
                onClick={() => window.open('https://maps.google.com/?q=Gemiya+Restaurant+France', '_blank')}
              >
                🗺️ {t('res.map.btn')}
              </button>
            </div>
          </div>

          {/* Find Us */}
          <div className="find-panel">
            <h3>{t('res.findus')}</h3>
            <div className="find-row"><span>📍</span><div><div className="find-label">{t('res.address.label')}</div><div className="find-val">Gemiya Restaurant<br/>France</div></div></div>
            <div className="find-row"><span>📞</span><div><div className="find-label">{t('phone.label')}</div><div className="find-val"><a href="tel:+33788313074">+33 7 88 31 30 74</a></div></div></div>
            <div className="find-row"><span>📧</span><div><div className="find-label">Email</div><div className="find-val">bonjour@gemiya.fr</div></div></div>
            <div className="field-label" style={{marginTop:'1rem',marginBottom:'.6rem'}}>{t('hours.label')}</div>
            <table className="hours-table">
              <tbody>
                <tr><td>{t('hours.monFri')}</td><td>11h30 – 22h30</td></tr>
                <tr><td>{t('hours.sat')}</td><td>11h30 – 23h00</td></tr>
                <tr><td>{t('hours.sun')}</td><td>12h00 – 22h00</td></tr>
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="res-summary">
            <h3>{t('res.summary.title')}</h3>
            <p>{t('res.summary.desc')}</p>
            <div className="summary-row"><span>📅</span><span>{summaryDate}</span></div>
            <div className="summary-row"><span>👥</span><span>{guests} {t('summary.guests_label')}</span></div>
            <div className="summary-row"><span>🕐</span><span>{selectedTime}</span></div>
            <div className="summary-row"><span>🍽️</span><span>{t(`occ.${occasion}`)}</span></div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
